import { useAppServices } from "hook/services";
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  Divider,
  LinearProgress,
  Chip
} from "@mui/material";
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import {
  Apps as AppsIcon,
  Business as BusinessIcon,
  TrendingUp as TrendingUpIcon,
  Star as StarIcon
} from '@mui/icons-material';

const Index = () => {
  const AppService = useAppServices();
  const [data, setData] = useState({
    topApps: [],
    topCompanies: []
  });
  const [loading, setLoading] = useState(true);
  const [daysFilter, setDaysFilter] = useState(7);

  const getData = async () => {
    try {
      setLoading(true);
      const { response } = await AppService.superadmin_users.app_logs({
        query: `day=${daysFilter}`
      });

      console.log(response, "logs data");
      setData({
        topApps: response.topApps || [],
        topCompanies: response.topCompanies || []
      });
    } catch (error) {
      console.error("Error fetching logs data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDaysFilterChange = (event) => {
    setDaysFilter(event.target.value);
  };

  useEffect(() => {
    getData();
  }, [daysFilter]);

  if (loading) {
    return (
      <DashboardLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      </DashboardLayout>
    );
  }

  // Calculate max count for percentage calculations
  const maxAppCount = data.topApps[0]?.count || 1;
  const maxCompanyCount = data.topCompanies[0]?.count || 1;

  return (
    <DashboardLayout>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
          <Typography variant="h4">Usage Analytics</Typography>
          <FormControl sx={{ minWidth: 120 }} size="small">
            <InputLabel>Time Period</InputLabel>
            <Select
              value={daysFilter}
              label="Time Period"
              onChange={handleDaysFilterChange}
            >
              <MenuItem value={1}>Last 1 day</MenuItem>
              <MenuItem value={7}>Last 7 days</MenuItem>
              <MenuItem value={30}>Last 30 days</MenuItem>
              <MenuItem value={90}>Last 90 days</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
          Data from the last {daysFilter} days
        </Typography>

        <Grid container spacing={3} sx={{ mt: 2 }}>
          {/* Top Apps Section */}
          <Grid item xs={12} md={6}>
            <Card elevation={3}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <AppsIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">Top Applications</Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />

                <List dense>
                  {data.topApps.map((app, index) => (
                    <ListItem key={app._id} sx={{ py: 1.5 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Avatar sx={{
                          bgcolor: index < 3 ? 'primary.main' : 'grey.300',
                          width: 24,
                          height: 24,
                          fontSize: '0.75rem'
                        }}>
                          {index + 1}
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body1" sx={{ fontWeight: index < 3 ? 600 : 400 }}>
                              {app.app_name}
                              <br />
                              {app._id}
                            </Typography>
                            <Chip
                              label={app.count.toLocaleString()}
                              size="small"
                              color={index < 3 ? "primary" : "default"}
                            />
                          </Box>
                        }
                        secondary={
                          <LinearProgress
                            variant="determinate"
                            value={(app.count / maxAppCount) * 100}
                            sx={{
                              height: 6,
                              borderRadius: 3,
                              mt: 1,
                              backgroundColor: 'grey.200',
                              '& .MuiLinearProgress-bar': {
                                backgroundColor: index < 3 ? 'primary.main' : 'grey.400'
                              }
                            }}
                          />
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Top Companies Section */}
          <Grid item xs={12} md={6}>
            <Card elevation={3}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <BusinessIcon color="secondary" sx={{ mr: 1 }} />
                  <Typography variant="h6">Top Companies</Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />

                <List dense>
                  {data.topCompanies.map((company, index) => (
                    <ListItem key={company._id} sx={{ py: 1.5 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Avatar sx={{
                          bgcolor: index < 3 ? 'secondary.main' : 'grey.300',
                          width: 24,
                          height: 24,
                          fontSize: '0.75rem'
                        }}>
                          {index + 1}
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body1" sx={{ fontWeight: index < 3 ? 600 : 400 }}>
                              {/* {company._id.substring(0, 6)}...{company._id.substring(company._id.length - 4)} */}
                              {company?.companyName}
                              <br />
                              {company._id}

                            </Typography>
                            <Chip
                              label={company.count.toLocaleString()}
                              size="small"
                              color={index < 3 ? "secondary" : "default"}
                            />
                          </Box>
                        }
                        secondary={
                          <LinearProgress
                            variant="determinate"
                            value={(company.count / maxCompanyCount) * 100}
                            sx={{
                              height: 6,
                              borderRadius: 3,
                              mt: 1,
                              backgroundColor: 'grey.200',
                              '& .MuiLinearProgress-bar': {
                                backgroundColor: index < 3 ? 'secondary.main' : 'grey.400'
                              }
                            }}
                          />
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        <Grid container spacing={3} sx={{ mt: 1 }}>

        </Grid>

        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            <Card elevation={2}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <TrendingUpIcon color="info" sx={{ mr: 1 }} />
                  <Typography variant="h6">App Highlights</Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  {data.topApps.slice(0, 3).map((app, index) => (
                    <Grid item xs={12} sm={4} key={app._id}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Avatar sx={{
                          bgcolor: 'primary.main',
                          width: 56,
                          height: 56,
                          mx: 'auto',
                          mb: 1
                        }}>
                          {index + 1}
                        </Avatar>
                        <Typography variant="subtitle1" noWrap>
                          {app.app_name}
                          {app.app_id}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {app.count.toLocaleString()} execution
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card elevation={2}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <TrendingUpIcon color="info" sx={{ mr: 1 }} />
                  <Typography variant="h6">Company Highlights</Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  {data.topCompanies.slice(0, 3).map((company, index) => (
                    <Grid item xs={12} sm={4} key={company._id}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Avatar sx={{
                          bgcolor: 'secondary.main',
                          width: 56,
                          height: 56,
                          mx: 'auto',
                          mb: 1
                        }}>
                          {index + 1}
                        </Avatar>
                        <Typography variant="subtitle1" noWrap>
                          {company?.companyName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {company.count.toLocaleString()} execution
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </DashboardLayout>
  );
};

export default Index;